import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Shield, Target, RefreshCw } from 'lucide-react';
import { useSubscribeToCopy, type CopySignal } from '@/hooks/use-copy-trade';
import { useMt5Accounts, useSyncMt5Balance } from '@/hooks/use-accounts';
import { Drawer } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { formatPnl, pnlColor } from '@/lib/format';

function dicebearAvatar(seed: string): string {
  return `https://api.dicebear.com/9.x/shapes/svg?seed=${encodeURIComponent(seed)}&backgroundColor=f97316,fb923c,fbbf24&shape1Color=f97316,ea580c`;
}

type AllocMode = 'FIXED_MULT' | 'FIXED_LOT' | 'EQUITY_RATIO';

export function CopyBotDrawer({ open, signal, onClose }: { open: boolean; signal: CopySignal; onClose: () => void }) {
  const navigate = useNavigate();
  const { data: accounts } = useMt5Accounts();
  const syncBalance = useSyncMt5Balance();
  const subscribe = useSubscribeToCopy();

  const [mt5AccountId, setMt5AccountId] = useState('');
  const [capital, setCapital] = useState(String(signal.min_capital ?? 100));
  const [allocMode, setAllocMode] = useState<AllocMode>('FIXED_MULT');
  const [fixedLot, setFixedLot] = useState('0.01');
  const [multiplier, setMultiplier] = useState('1');
  const [slPct, setSlPct] = useState(String(signal.default_sl_pct || 20));
  const [tpEnabled, setTpEnabled] = useState(!!signal.default_tp_pct);
  const [tpPct, setTpPct] = useState(String(signal.default_tp_pct || 50));

  const selectedAccount = (accounts ?? []).find(a => a.id === mt5AccountId);
  const capitalNum = parseFloat(capital) || 0;
  const exceedsCapital = !!selectedAccount && capitalNum > 0 && capitalNum > (selectedAccount.unallocated_capital ?? 0);
  const belowMinCapital = signal.min_capital != null && capitalNum > 0 && capitalNum < signal.min_capital;
  const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const firstAccount = accounts?.[0];
  if (!mt5AccountId && firstAccount) setMt5AccountId(firstAccount.id);

  // provider_equity is used by the backend for EQUITY_RATIO computation
  // but we don't display it to copiers (privacy + Vantage API limitation)

  const handleSubmit = async () => {
    if (!mt5AccountId || exceedsCapital || belowMinCapital) return;
    try {
      const result = await subscribe.mutateAsync({
        signalId: signal.id,
        mt5AccountId,
        allocationMode: allocMode,
        ...(allocMode === 'FIXED_LOT' && { fixedLot: parseFloat(fixedLot) }),
        ...((allocMode === 'FIXED_MULT' || allocMode === 'EQUITY_RATIO') && { multiplier: parseFloat(multiplier) }),
        assignedCapital: capitalNum,
        slPct: parseFloat(slPct),
        tpPct: tpEnabled ? parseFloat(tpPct) : null,
      });
      onClose();
      navigate(`/bots/${result.copierBot?.id || result.id}`);
    } catch {}
  };

  const avatarUrl = signal.avatar_url || dicebearAvatar(signal.name);

  return (
    <Drawer open={open} title="Copy" onClose={onClose}>
      <div className="space-y-5">
        {/* Signal info */}
        <div className="flex items-center gap-3 p-3 glass-sm rounded-lg border border-orange/20">
          <img src={avatarUrl} alt="" className="w-10 h-10 rounded-full bg-surface flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-ink">{signal.name}</p>
            {signal.description && <p className="text-[10px] text-ink-4 mt-0.5 line-clamp-1">{signal.description}</p>}
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[9px] font-mono font-semibold text-orange bg-orange/10 rounded px-1.5 py-0.5">
                {signal.symbol_filter?.join(', ') || 'All Symbols'}
              </span>
              <span className="text-[9px] text-ink-4">{signal.total_trades} trades</span>
              <span className={`text-[9px] font-mono font-semibold ${pnlColor(signal.total_pnl)}`}>{formatPnl(signal.total_pnl)}</span>
            </div>
          </div>
        </div>

        {/* MT5 Account */}
        <div>
          <label className="text-xs font-semibold text-ink-3 block mb-1">MT5 Account *</label>
          <select value={mt5AccountId} onChange={e => setMt5AccountId(e.target.value)}
            className="w-full bg-surface border border-border rounded-md px-3 py-2 text-sm text-ink focus:outline-none focus:border-orange">
            <option value="">Select account...</option>
            {(accounts ?? []).map(a => (
              <option key={a.id} value={a.id}>{a.name || a.login_id} {a.synced_balance != null ? `($${fmt(a.synced_balance)})` : ''}</option>
            ))}
          </select>
          {selectedAccount && (
            <div className="mt-2 glass-sm rounded-md p-2.5 text-[10px] space-y-1">
              <div className="flex justify-between">
                <span className="text-ink-4">Balance</span>
                <span className="font-mono font-semibold text-ink">${fmt(selectedAccount.synced_balance ?? 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-4">Available</span>
                <div className="flex items-center gap-1">
                  <span className={`font-mono font-semibold ${(selectedAccount.unallocated_capital ?? 0) <= 0 ? 'text-red' : 'text-green'}`}>
                    ${fmt(selectedAccount.unallocated_capital ?? 0)}
                  </span>
                  <button onClick={() => syncBalance.mutate(selectedAccount.id)} disabled={syncBalance.isPending}
                    className="text-ink-4 hover:text-orange transition-colors cursor-pointer disabled:opacity-50" title="Sync balance">
                    <RefreshCw size={9} className={syncBalance.isPending ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-4">Leverage</span>
                <span className="font-mono font-semibold text-ink">1:{selectedAccount.leverage ?? 100}</span>
              </div>
            </div>
          )}
        </div>

        {/* Capital */}
        <div>
          <label className="text-xs font-semibold text-ink-3 block mb-1">Assigned Capital ($) *</label>
          <Input type="number" value={capital} onChange={e => setCapital(e.target.value)} min="10" step="10"
            className={exceedsCapital || belowMinCapital ? '!border-red' : ''} />
          {exceedsCapital && <p className="text-[10px] text-red mt-0.5">Exceeds available by ${fmt(capitalNum - (selectedAccount?.unallocated_capital ?? 0))}</p>}
          {belowMinCapital && <p className="text-[10px] text-red mt-0.5">This signal requires a minimum of ${fmt(signal.min_capital!)} to copy</p>}
          {signal.min_capital != null && !belowMinCapital && !exceedsCapital && (
            <p className="text-[10px] text-ink-4 mt-0.5">Minimum capital: ${fmt(signal.min_capital)}</p>
          )}
        </div>

        {/* Allocation Mode */}
        <div>
          <label className="text-xs font-semibold text-ink-3 block mb-1">Lot Sizing</label>
          <div className="grid grid-cols-3 gap-2">
            {([
              { key: 'FIXED_MULT' as const, label: 'Multiplier', desc: 'Scale provider volume' },
              { key: 'FIXED_LOT' as const, label: 'Fixed Lots', desc: 'Same lot every trade' },
              { key: 'EQUITY_RATIO' as const, label: 'Equity Ratio', desc: 'Proportional to equity' },
            ]).map(m => (
              <button key={m.key} type="button"
                onClick={() => setAllocMode(m.key)}
                className={`text-left rounded-lg border px-3 py-2 transition-all cursor-pointer ${
                  allocMode === m.key
                    ? 'border-orange/40 bg-orange/5'
                    : 'border-glass-border bg-surface/50 hover:bg-surface'
                }`}>
                <span className={`text-[11px] font-bold ${allocMode === m.key ? 'text-orange' : 'text-ink-3'}`}>
                  {m.label}
                </span>
                <p className="text-[9px] text-ink-4 mt-0.5">{m.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {allocMode === 'FIXED_MULT' && (
          <div>
            <label className="text-xs font-semibold text-ink-3 block mb-1">Multiplier *</label>
            <Input type="number" value={multiplier} onChange={e => setMultiplier(e.target.value)} min="0.1" step="0.1" />
            <p className="text-[10px] text-ink-4 mt-1">Copier volume = provider volume × {multiplier || '1'}</p>
          </div>
        )}
        {allocMode === 'FIXED_LOT' && (
          <div>
            <label className="text-xs font-semibold text-ink-3 block mb-1">Lot Size *</label>
            <Input type="number" value={fixedLot} onChange={e => setFixedLot(e.target.value)} min="0.01" step="0.01" />
            <p className="text-[10px] text-ink-4 mt-1">Every copied trade uses this exact lot size</p>
          </div>
        )}
        {allocMode === 'EQUITY_RATIO' && (
          <div className="space-y-3">
            <p className="text-[10px] text-ink-4">
              Trades will be scaled proportionally to your capital. Larger capital means larger positions, smaller capital means smaller positions.
            </p>
            <div>
              <label className="text-xs font-semibold text-ink-3 block mb-1">Multiplier</label>
              <Input type="number" value={multiplier} onChange={e => setMultiplier(e.target.value)} min="0.1" step="0.1" />
              <p className="text-[10px] text-ink-4 mt-1">
                {parseFloat(multiplier) === 1 ? 'Standard scaling (1×)' :
                 parseFloat(multiplier) < 1 ? `Conservative — ${multiplier}× smaller positions` :
                 `Aggressive — ${multiplier}× larger positions`}
              </p>
            </div>
          </div>
        )}

        {/* Risk Management */}
        <div className="space-y-3">
          <p className="text-[11px] font-bold text-ink-3 uppercase tracking-wider">Risk Management</p>

          <div className="bg-surface/50 rounded-lg p-3 border border-red/10">
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] font-semibold text-ink-3 flex items-center gap-1.5">
                <Shield size={10} className="text-red" /> Stop Loss
              </label>
              <span className="text-sm font-extrabold font-mono text-red">{slPct}%</span>
            </div>
            <input type="range" min="5" max="95" value={slPct} onChange={e => setSlPct(e.target.value)}
              className="w-full accent-red h-1 rounded-full cursor-pointer" />
            <div className="flex justify-between mt-1">
              <span className="text-[8px] text-ink-4 font-mono">5%</span>
              <span className="text-[8px] text-ink-4 font-mono">95%</span>
            </div>
            {capitalNum > 0 && (
              <div className="mt-2 pt-2 border-t border-red/5 flex justify-between">
                <span className="text-[9px] text-ink-4">Max loss</span>
                <span className="text-[11px] font-bold font-mono text-red">-${fmt(capitalNum * parseInt(slPct) / 100)}</span>
              </div>
            )}
          </div>

          <div className={`rounded-lg p-3 border transition-colors ${tpEnabled ? 'bg-surface/50 border-green/10' : 'bg-surface/30 border-glass-border/30'}`}>
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-semibold text-ink-3 flex items-center gap-1.5">
                <Target size={10} className={tpEnabled ? 'text-green' : 'text-ink-4'} /> Take Profit
              </label>
              <div className="flex items-center gap-2">
                {tpEnabled && <span className="text-sm font-extrabold font-mono text-green">{tpPct}%</span>}
                <button type="button" onClick={() => setTpEnabled(!tpEnabled)}
                  className={`w-8 h-4 rounded-full transition-colors cursor-pointer relative ${tpEnabled ? 'bg-green' : 'bg-surface-2'}`}>
                  <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${tpEnabled ? 'left-4' : 'left-0.5'}`} />
                </button>
              </div>
            </div>
            {!tpEnabled && <p className="text-[9px] text-ink-4 mt-1">No profit target — bot runs until manually stopped.</p>}
            {tpEnabled && (
              <div className="mt-1">
                <input type="range" min="5" max="95" value={tpPct} onChange={e => setTpPct(e.target.value)}
                  className="w-full accent-green h-1 rounded-full cursor-pointer mt-2" />
                <div className="flex justify-between mt-1">
                  <span className="text-[8px] text-ink-4 font-mono">5%</span>
                  <span className="text-[8px] text-ink-4 font-mono">95%</span>
                </div>
                {capitalNum > 0 && (
                  <div className="mt-2 pt-2 border-t border-green/5 flex justify-between">
                    <span className="text-[9px] text-ink-4">Target gain</span>
                    <span className="text-[11px] font-bold font-mono text-green">+${fmt(capitalNum * parseInt(tpPct) / 100)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <Button onClick={handleSubmit} disabled={!mt5AccountId || subscribe.isPending || exceedsCapital || belowMinCapital} className="w-full">
          {subscribe.isPending ? <Spinner size="sm" /> : 'Start Copying'}
        </Button>
      </div>
    </Drawer>
  );
}
